import { createFileRoute, Link } from "@tanstack/react-router";

import dannyCurrentAsset from "@/assets/danny-portrait-1.jpeg.asset.json";
import introVideoAsset from "@/assets/danny-intro.mp4.asset.json";
import mainLogoAsset from "@/assets/danny-main-logo.jpg.asset.json";
import forageMofoCertAsset from "@/assets/forage-mofo-litigation-cert.jpg.asset.json";
import forageKpmgCertAsset from "@/assets/forage-kpmg-audit-cert.jpg.asset.json";
import {
  ShieldCheck,
  BadgeCheck,
  Eye,
  Scale,
  Sparkles,
  GraduationCap,
  MapPin,
  Users,
  Lightbulb,
  Trophy,
  Mic,
  School,
  Briefcase,
  BookOpen,
  Landmark,
} from "lucide-react";
import { Disclaimer } from "@/components/site/Disclaimer";

const personJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Person",
      "@id": "https://nkadimengdannylcs.com/about#person",
      name: "Nkadimeng Danny",
      givenName: "Danny",
      familyName: "Nkadimeng",
      url: "https://nkadimengdannylcs.com/about",
      mainEntityOfPage: "https://nkadimengdannylcs.com/about",
      jobTitle: "Legal & Compliance Support Practitioner",
      description:
        "LLB graduate (University of South Africa) and two-time published author in Record of Law for legal research and writing. Provides legal information and legal advice on non-reserved matters, legal opinions, paralegal, drafting and compliance support across South Africa.",
      nationality: { "@type": "Country", name: "South Africa" },
      knowsLanguage: ["English", "Sepedi", "Zulu", "Xhosa", "Afrikaans"],
      knowsAbout: [
        "Legal research and writing",
        "Labour law and CCMA procedure",
        "Contract drafting",
        "POPIA and regulatory compliance",
        "Company secretarial practice",
      ],
      alumniOf: {
        "@type": "CollegeOrUniversity",
        name: "University of South Africa",
        sameAs: "https://www.unisa.ac.za/",
      },
      hasCredential: [
        {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "degree",
          name: "Bachelor of Laws (LLB)",
          educationalLevel: "Bachelor's degree",
          recognizedBy: {
            "@type": "CollegeOrUniversity",
            name: "University of South Africa",
          },
        },
        {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "publication record",
          name: "Two-time published author — Record of Law (legal research and writing)",
          description:
            "Published twice in Record of Law for legal research and writing.",
        },
      ],
      award: [
        "Two-time published author in Record of Law for legal research and writing",
      ],
      worksFor: {
        "@type": "LegalService",
        name: "Nkadimeng Danny Legal & Compliance Support",
        url: "https://nkadimengdannylcs.com",
      },
    },
    {
      "@type": "AboutPage",
      "@id": "https://nkadimengdannylcs.com/about#about",
      url: "https://nkadimengdannylcs.com/about",
      name: "About Nkadimeng Danny Legal & Compliance Support",
      mainEntity: { "@id": "https://nkadimengdannylcs.com/about#person" },
      about: { "@id": "https://nkadimengdannylcs.com/about#person" },
    },
  ],
};

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Us | Nkadimeng Danny Legal & Compliance Support" },
      {
        name: "description",
        content:
          "Meet Nkadimeng Danny — LLB graduate (UNISA) and two-time published author in Record of Law for legal research and writing — plus our mission, vision and values.",
      },
      { property: "og:title", content: "About Nkadimeng Danny Legal & Compliance Support" },
      {
        property: "og:description",
        content: "Our mission, vision, values and founder profile.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(personJsonLd),
      },
    ],
  }),
  component: AboutPage,
});


const values = [
  { icon: ShieldCheck, label: "Integrity", body: "We do what's right, even when it isn't easy." },
  { icon: BadgeCheck, label: "Professionalism", body: "Punctual, prepared and respectful in every interaction." },
  { icon: Scale, label: "Accountability", body: "Clear scope, clear timelines, clear ownership of outcomes." },
  { icon: Eye, label: "Confidentiality", body: "Your matter stays private — POPIA-aligned at every step." },
  { icon: Sparkles, label: "Excellence", body: "Attention to detail in drafting, preparation and presentation." },
];

const stats = [
  { value: "LLB", label: "University of South Africa" },
  { value: "100%", label: "Remote & in-person" },
  { value: "1 day", label: "Typical response time" },
  { value: "POPIA", label: "Compliant handling" },
];

function AboutPage() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-24">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            About us
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            A South African legal support and compliance practice on a mission
            to make professional assistance affordable, accessible and
            transparent for everyone.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-border bg-card p-5 shadow-soft">
                <p className="font-display text-2xl text-navy">{s.value}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-narrow grid gap-12 py-20 md:grid-cols-2">
        <div>
          <h2 className="font-display text-2xl text-navy">Our mission</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            To provide accessible, reliable and professional legal support and
            compliance services that empower individuals and businesses to
            navigate legal and regulatory challenges confidently — without
            the gatekeeping, jargon and cost barriers that often stand in the
            way.
          </p>
        </div>
        <div>
          <h2 className="font-display text-2xl text-navy">Our vision</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            To be a trusted legal-support partner recognised for excellence,
            integrity and client-focused solutions — and a respected referral
            partner to admitted attorneys, mediators and compliance
            professionals across South Africa.
          </p>
        </div>
      </section>

      <section className="bg-secondary/50 py-20">
        <div className="container-narrow">
          <h2 className="font-display text-2xl text-navy">Our values</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {values.map((v) => (
              <div
                key={v.label}
                className="rounded-xl border border-border bg-card p-5 shadow-soft"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
                  <v.icon className="h-5 w-5" />
                </div>
                <div className="mt-3 font-display text-lg text-navy">
                  {v.label}
                </div>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-narrow py-20">
        <div className="grid gap-12 md:grid-cols-[1fr_1.4fr] md:items-center">
          <div className="overflow-hidden rounded-2xl border border-border shadow-lifted">
            <img
              src={mainLogoAsset.url}
              alt="Nkadimeng Danny Legal & Compliance Support — golden ND monogram with scales of justice"
              width={1254}
              height={1254}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <span className="gold-rule" />
            <h2 className="font-display text-3xl text-navy md:text-4xl">
              Nkadimeng Danny
            </h2>
            <p className="mt-2 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-gold">
              <GraduationCap className="h-4 w-4" /> Founder · LLB (UNISA) ·
              Two-time published, Record of Law
            </p>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Nkadimeng Danny is the founder of Nkadimeng Danny Legal and
              Compliance Support, an LLB graduate from the University of
              South Africa, and a two-time published author in Record of Law
              for legal research and writing. He grew up in rural Limpopo, learned the value of

              speaking up at school debates, served his community as a Learner
              Representative Council President, and worked his way through ten
              years of customer-facing roles — at McDonald's, Shoprite, Cash
              Crusaders and Boxer Superstores — before stepping into law
              full-time. Every one of those chapters shaped the practice you
              see today: disciplined, plain-spoken and grounded in real life.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              His focus is labour relations, CCMA preparation, contract and
              affidavit drafting, POPIA and consumer-law compliance, and
              filings on Court Online and CaseLines. He works with employees,
              employers, small businesses and individuals — guiding them
              through complex matters with clarity, professionalism and care.
              Where a matter calls for an admitted attorney, Danny refers
              clients to trusted practitioners in his network and continues
              to support the file in the background to keep costs down.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Outside the office, Danny remains a student of South African
              constitutional history, a regular voice on community legal
              education, and a believer that good law should be readable
              before it is reverenced.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                { icon: MapPin, label: "Based in South Africa, serving nationally" },
                { icon: Users, label: "Works with individuals, SMEs & HR teams" },
                { icon: Lightbulb, label: "Plain-English approach to legal issues" },
                { icon: ShieldCheck, label: "Confidential, POPIA-aligned handling" },
              ].map((b) => (
                <div key={b.label} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <b.icon className="mt-0.5 h-4 w-4 text-gold" />
                  <span>{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Director video + gallery */}
      <section className="container-narrow py-16">
        <span className="gold-rule" />
        <h2 className="font-display text-3xl text-navy md:text-4xl">
          A word from the Director
        </h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Press play to hear directly from Danny — in his own voice, in under
          a minute — on why this practice exists and who it is built for.
        </p>

        <div className="mt-8 grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-start">
          <div className="overflow-hidden rounded-2xl border border-border bg-black shadow-lifted">
            <video
              src={introVideoAsset.url}
              poster={dannyCurrentAsset.url}
              controls
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
              className="aspect-video h-full w-full object-cover"
            >
              <track
                kind="captions"
                src="/captions/danny-intro.en.vtt"
                srcLang="en"
                label="English"
                default
              />
              Your browser does not support embedded video.
            </video>
          </div>
          <div className="overflow-hidden rounded-xl border border-border shadow-soft">
            <img
              src={dannyCurrentAsset.url}
              alt="Nkadimeng Danny — Director portrait"
              loading="lazy"
              className="aspect-[3/4] h-full w-full object-cover transition-transform duration-500 hover:scale-105"
            />
          </div>
        </div>

        {/* Transcript — accessibility + SEO */}
        <details className="mt-8 rounded-xl border border-border bg-card p-5 shadow-soft">
          <summary className="cursor-pointer font-display text-lg text-navy">
            Read the transcript
          </summary>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
            <p>
              Hello, I'm Nkadimeng Danny, founder of Nkadimeng Danny Legal and
              Compliance Support.
            </p>
            <p>
              I'm an LLB graduate of the University of South Africa, and I
              built this practice for one reason — to make professional legal
              and compliance support genuinely reachable for ordinary South
              Africans.
            </p>
            <p>
              Whether it's a labour dispute, a CCMA matter, a contract that
              needs drafting, or POPIA compliance — we deliver it with a clear
              scope, fair pricing, and no jargon.
            </p>
            <p>
              If your matter calls for an admitted attorney, we'll tell you up
              front and refer you to a trusted practitioner.
            </p>
            <p>Thank you for visiting — I look forward to standing in your corner.</p>
          </div>
        </details>

        {/* Meet the Director CTA */}
        <div className="mt-8 flex flex-wrap items-center gap-3 rounded-2xl border border-gold/40 bg-secondary/40 p-6 shadow-soft">
          <div className="flex-1 min-w-[240px]">
            <h3 className="font-display text-xl text-navy">
              Want to speak with the Director?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Book a one-on-one consultation with Danny — in person, by phone,
              Microsoft Teams or Zoom.
            </p>
          </div>
          <Link
            to="/book"
            className="inline-flex items-center justify-center rounded-md bg-gold px-5 py-2.5 text-sm font-semibold text-gold-foreground shadow-gold hover:brightness-95 transition"
          >
            Meet the Director — Book consultation
          </Link>
        </div>
      </section>

      {/* The Journey */}

      <section className="border-y border-border bg-secondary/40 py-20">
        <div className="container-narrow">
          <span className="gold-rule" />
          <h2 className="font-display text-3xl text-navy md:text-4xl">
            The long road to law
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground leading-relaxed">
            Nobody handed me this LLB. It was carried, one season at a time,
            from a small classroom in Limpopo to a graduation stage at UNISA —
            through dusty school halls, late retail shifts, five rejection
            letters, and the quiet, stubborn belief that justice should be
            within reach of ordinary South Africans. This is that road.
          </p>

          <ol className="relative mt-10 border-l border-gold/30 pl-6">
            {[
              {
                icon: School,
                title: "Mpheleng Primary School — where the foundation was laid",
                body:
                  "Before there were case names or statutes, there were the basics: how to read a sentence carefully, how to sit still and listen, how to arrive prepared even when nobody was watching. Those small habits became the spine of everything that followed.",
              },
              {
                icon: Mic,
                title: "Sebakanaga Secondary — the first time I found my voice",
                body:
                  "I joined the school's debate team not knowing what to expect. In my very first debate I was named Best Speaker — number one. Walking back to my seat, I realised words could carry weight, and that a well-built argument can shift a room. That single moment quietly redirected my life.",
              },
              {
                icon: Mic,
                title: "Moutse Community Radio — speaking to a public, not a panel",
                body:
                  "Representing the school on community radio meant strangers across the district were listening. It taught me to slow down, to choose words that respect the other side, and to keep my nerve when the red 'on air' light came on. To this day, I argue the same way: firmly, but with respect.",
              },
              {
                icon: Landmark,
                title: "Liliesleaf Farm, Rivonia — the day law became a calling",
                body:
                  "Chosen on History results to visit Liliesleaf, the farm at the centre of the Rivonia Trial, I stood in the very rooms where ordinary people had risked everything so that one day a child like me could be free to argue in a courtroom. Something settled in me there. Law stopped being an idea and became a duty.",
              },
              {
                icon: Trophy,
                title: "2014 — Matric, and elected LRC President",
                body:
                  "In my final year of school my peers elected me Learner Representative Council President. I helped draft the LRC constitution, structures and rules of conduct still in use at the school today. It was my first lesson in something that has guided me ever since: good rules, fairly applied, can change how an entire community treats one another.",
              },
              {
                icon: Briefcase,
                title: "Ten years on the shop floor — McDonald's, Shoprite, Cash Crusaders, Boxer",
                body:
                  "While many of my classmates went straight to university, I went to work. Nearly a decade across four major retailers — early mornings, late nights, demanding customers, real conflict, real consequences. I learned to defuse a tense till point, to listen before I responded, and eventually to stand up and represent my colleagues in internal disciplinary hearings at Boxer Superstores. Long before I held a degree, those years taught me what most law schools cannot: how working South Africans actually live, and how badly they need someone in their corner.",
              },
              {
                icon: BookOpen,
                title: "Five universities. One ‘yes’. And it wasn't for Law.",
                body:
                  "I applied to five universities for LLB and BEd. Only one came back with an offer — and it was for Education, not Law. It would have been the easier road to take. I didn't take it. In September 2018 I applied to UNISA, was accepted, and began the LLB in 2019. NSFAS came through with funding at the exact moment I needed it. I have never forgotten how close I came to never studying law at all.",
              },
              {
                icon: GraduationCap,
                title: "Today — LLB graduate, UNISA alumnus, and only beginning",
                body:
                  "I now hold the LLB I once feared might never come. I serve South Africans with the same access-to-justice conviction that started in a school debate hall in Limpopo. I am working toward formal admission, and in the meantime I am building this practice — paralegal, compliance and community legal support — so that the next person who feels shut out of the system has somewhere to turn first.",
              },
            ].map((step) => (
              <li key={step.title} className="mb-8 last:mb-0">
                <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-navy text-gold ring-4 ring-secondary/40">
                  <step.icon className="h-3.5 w-3.5" />
                </span>
                <h3 className="font-display text-lg text-navy">{step.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Awards & recognition */}
      <section className="container-narrow py-16">
        <span className="gold-rule" />
        <h2 className="font-display text-3xl text-navy md:text-4xl">
          Awards & recognition
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {[
            {
              icon: Trophy,
              title: "Best Speaker — No. 1",
              body: "Awarded in my first ever school debate at Sebakanaga Secondary School.",
            },
            {
              icon: Mic,
              title: "Moutse Community Radio Debater",
              body: "Selected to represent the school on a community radio debate platform.",
            },
            {
              icon: Landmark,
              title: "Liliesleaf Heritage Delegate",
              body: "Chosen on History merit to visit Liliesleaf, Rivonia and learn the story of our struggle heroes.",
            },
            {
              icon: Users,
              title: "LRC President — 2014",
              body: "Elected by peers; the structures and rules established that year are still used at the school.",
            },
            {
              icon: GraduationCap,
              title: "UNISA Alumni",
              body: "LLB graduate of the University of South Africa.",
            },
            {
              icon: BookOpen,
              title: "Two-time published — Record of Law",
              body: "Published twice in Record of Law for legal research and writing.",
            },

            {
              icon: BadgeCheck,
              title: "10 years customer-facing service",
              body: "Across McDonald's, Shoprite, Cash Crusaders and Boxer Superstores — including internal disciplinary representation.",
            },
            {
              icon: Briefcase,
              title: "Litigation Job Simulation — Morrison & Foerster (via Forage)",
              body: "Completed the Morrison & Foerster Litigation virtual job simulation on Forage (July 2026), covering trade secrets analysis and advising senior executives. Verification code 6a513171a0e8399451caf85d.",
            },
            {
              icon: Briefcase,
              title: "Audit Job Simulation — KPMG (via Forage)",
              body: "Completed the KPMG Audit virtual job simulation on Forage (July 2026), covering audit planning, risk assessment, and revenue analytics testing. Enrolment code 6a60a25d9c63ff070bfa8846.",
            },
          ].map((a) => (
            <div
              key={a.title}
              className="rounded-xl border border-border bg-card p-5 shadow-soft"
            >
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
                <a.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-3 font-display text-lg text-navy">{a.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                {a.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* International practical training */}
      <section className="border-y border-border bg-secondary/40 py-16">
        <div className="container-narrow grid gap-8 md:grid-cols-2 md:items-center">
          <div>
            <span className="gold-rule" />
            <h2 className="font-display text-3xl text-navy md:text-4xl">
              International practical training
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              In July 2026 the Director completed the{" "}
              <strong className="text-navy">Morrison &amp; Foerster Litigation Job Simulation</strong>{" "}
              on Forage — a practical programme run by one of the world's leading
              international law firms. The simulation covered{" "}
              <em>Trade Secrets</em> analysis and{" "}
              <em>Advising Senior Executives on Trade Secrets</em>, mirroring the
              real work an associate would tackle in a US litigation practice.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
              <li>• Recipient: <strong className="text-navy">Danny Nkadimeng</strong></li>
              <li>• Programme: Litigation Job Simulation</li>
              <li>• Issued by: Morrison &amp; Foerster LLP via Forage</li>
              <li>• Date: 21 July 2026</li>
              <li>• Enrolment code: <span className="font-mono">uHmCpoys9vy7GrSzH</span></li>
              <li>• Verification code: <span className="font-mono">6a513171a0e8399451caf85d</span></li>
            </ul>
            <p className="mt-4 text-xs text-muted-foreground italic">
              This is an independent practical credential and does not represent
              admission to practise in the United States or an employment
              relationship with Morrison &amp; Foerster LLP.
            </p>
          </div>
          <a
            href={forageMofoCertAsset.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-xl border border-border bg-card p-3 shadow-soft hover:shadow-md transition"
          >
            <img
              src={forageMofoCertAsset.url}
              alt="Certificate of Completion — Morrison & Foerster Litigation Job Simulation issued to Danny Nkadimeng by Forage, July 2026"
              className="w-full rounded-md"
              loading="lazy"
              width={1920}
              height={1358}
            />
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Tap to view the full certificate
            </p>
          </a>
        </div>
      </section>

      {/* KPMG Audit simulation */}
      <section className="border-b border-border bg-background py-16">
        <div className="container-narrow grid gap-8 md:grid-cols-2 md:items-center">
          <a
            href={forageKpmgCertAsset.url}
            target="_blank"
            rel="noopener noreferrer"
            className="order-2 md:order-1 block rounded-xl border border-border bg-card p-3 shadow-soft hover:shadow-md transition"
          >
            <img
              src={forageKpmgCertAsset.url}
              alt="Certificate of Completion — KPMG Audit Job Simulation issued to Danny Nkadimeng by Forage, July 2026"
              className="w-full rounded-md"
              loading="lazy"
              width={1920}
              height={1358}
            />
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Tap to view the full certificate
            </p>
          </a>
          <div className="order-1 md:order-2">
            <span className="gold-rule" />
            <h2 className="font-display text-3xl text-navy md:text-4xl">
              Audit &amp; assurance training
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              In July 2026 the Director also completed the{" "}
              <strong className="text-navy">KPMG Audit Job Simulation</strong>{" "}
              on Forage — a practical programme delivered by one of the Big Four
              professional services firms. The simulation covered{" "}
              <em>Planning the audit — risk assessment</em> and{" "}
              <em>Testing — revenue analytics</em>, giving hands-on exposure to
              the workflow of a first-year audit associate.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
              <li>• Recipient: <strong className="text-navy">Danny Nkadimeng</strong></li>
              <li>• Programme: Audit Job Simulation</li>
              <li>• Issued by: KPMG via Forage</li>
              <li>• Date: 22 July 2026</li>
              <li>• Enrolment code: <span className="font-mono">6a60a25d9c63ff070bfa8846</span></li>
              <li>• User verification code: <span className="font-mono">6a513171a0e8399451caf85d</span></li>
            </ul>
            <p className="mt-4 text-xs text-muted-foreground italic">
              This is an independent practical credential and does not represent
              a licence to perform statutory audits or an employment relationship
              with KPMG.
            </p>
          </div>
        </div>
      </section>




      <section className="container-narrow pb-16">
        <Disclaimer variant="highlight" />
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {[
            {
              title: "What we do",
              body: "Paralegal support, legal drafting, compliance advisory, CCMA case preparation, debt negotiation and Court Online training.",
            },
            {
              title: "What we don't do",
              body: "We do not represent clients in court, at the CCMA, at bargaining councils or in internal hearings. We do not draft reserved documents or hold client funds in trust.",
            },
            {
              title: "When we refer",
              body: "If your matter requires an admitted attorney, advocate or debt counsellor, we say so up front and refer you to a trusted practitioner.",
            },
          ].map((c) => (
            <div key={c.title} className="rounded-xl border border-border bg-card p-6 shadow-soft">
              <h3 className="font-display text-lg text-navy">{c.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-secondary/40">
        <div className="container-narrow py-16">
          <span className="gold-rule" />
          <h2 className="font-display text-3xl text-navy md:text-4xl">
            Our promise to you
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground leading-relaxed">
            Every brief is researched against current South African law,
            double-checked before it leaves us, and escalated to an admitted
            attorney the moment your matter calls for one. You get
            attorney-level preparation at a fraction of the cost — without
            ever being told what you want to hear instead of what the law
            actually says.
          </p>

          <div className="mt-10 rounded-xl border border-gold/40 bg-card p-6 shadow-soft md:p-8">
            <h3 className="font-display text-xl text-navy">
              How we keep your matter on track
            </h3>
            <ul className="mt-4 grid gap-3 text-sm text-muted-foreground leading-relaxed md:grid-cols-2">
              <li>• Fixed scope and clear turnaround on every brief</li>
              <li>• POPIA-aligned handling of every document you send us</li>
              <li>• Plain-English explanations — no Latin, no jargon dumps</li>
              <li>• A written hand-over pack at the end of every matter</li>
              <li>• Honest referrals to admitted attorneys where required</li>
              <li>• A confidential client portal for documents and updates</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/services/$slug"
                params={{ slug: "drafting-paralegal" }}

                className="inline-flex items-center justify-center rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground"
              >
                See drafting & paralegal services
              </Link>
              <Link
                to="/book"
                className="inline-flex items-center justify-center rounded-md border border-input bg-background px-5 py-2.5 text-sm font-medium text-foreground"
              >
                Book a consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-narrow py-12">

        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/book"
            className="inline-flex items-center justify-center rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground"
          >
            Book a consultation
          </Link>
          <Link
            to="/disclaimer"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-5 py-2.5 text-sm font-medium text-foreground"
          >
            Read full disclaimer
          </Link>
        </div>
      </section>
    </>
  );
}
